import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { cardService } from '../../services/cardService';
import { paymentService } from '../../services/payment';
import { Student, Transaction } from '../../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const [students, setStudents] = useState<Student[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalBalance: 0,
    activeStudents: 0,
    monthlySpending: 0
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [userStudents, userTransactions] = await Promise.all([
          cardService.getUserStudents(),
          paymentService.getUserTransactions()
        ]);

        setStudents(userStudents);
        setTransactions(userTransactions.slice(0, 5));

        const totalBalance = userStudents.reduce(
          (sum, s) => sum + s.balance,
          0
        );

        const activeStudents = userStudents.filter(
          s => s.currentCardId !== null
        ).length;

        const monthlySpending = userTransactions
          .filter(t => t.type === 'purchase')
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);

        setStats({ totalBalance, activeStudents, monthlySpending });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Welcome back, {user?.name}
        </p>
      </div>

      {/* SUMMARY CARDS (IMPORTANT) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        <div className="bg-white shadow rounded-lg p-4 border">
          <p className="text-sm text-gray-500">Total Balance</p>
          <p className="text-xl font-semibold text-green-600">
            PKR {stats.totalBalance.toFixed(2)}
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-4 border">
          <p className="text-sm text-gray-500">Active Cards</p>
          <p className="text-xl font-semibold text-gray-900">
            {stats.activeStudents}
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-4 border">
          <p className="text-sm text-gray-500">Monthly Spending</p>
          <p className="text-xl font-semibold text-red-500">
            PKR {stats.monthlySpending.toFixed(2)}
          </p>
        </div>
      </div>

      {/* PRIMARY ACTIONS */}
      <div className="grid grid-cols-3 gap-3">

        <Link
          to="/topup"
          className="bg-blue-600 text-white text-center py-3 rounded-lg text-sm font-medium"
        >
          Top Up
        </Link>

        <Link
          to="/cards"
          className="bg-white border text-center py-3 rounded-lg text-sm font-medium"
        >
          Cards
        </Link>

        <Link
          to="/history"
          className="bg-white border text-center py-3 rounded-lg text-sm font-medium"
        >
          History
        </Link>
      </div>

      {/* RECENT TRANSACTIONS (CLEAN LIST) */}
      <div className="bg-white shadow rounded-lg p-4 border">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">
          Recent Activity
        </h2>

        <div className="space-y-3">
          {transactions.map(t => (
            <div
              key={t.id}
              className="flex justify-between items-center"
            >
              <div>
                <p className="text-sm text-gray-700">
                  {t.description}
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(t.timestamp).toLocaleString()}
                </p>
              </div>

              <p className={`text-sm font-semibold ${
                t.amount >= 0 ? 'text-green-600' : 'text-red-500'
              }`}>
                {t.amount >= 0 ? '+' : '-'}PKR {Math.abs(t.amount)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CHILDREN / USERS (CARD STYLE CLEAN) */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">
          Users
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          {students.map(s => (
            <div
              key={s.id}
              className="bg-white border rounded-lg p-4"
            >

              <div className="flex justify-between">
                <p className="font-medium text-gray-900">
                  {s.name}
                </p>

                <span className={`text-xs px-2 py-1 rounded-full ${
                  s.currentCardId
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {s.currentCardId ? 'Active' : 'No Card'}
                </span>
              </div>

              <p className="text-sm text-gray-500 mt-1">
                ID: {s.studentId}
              </p>

              <p className="text-lg font-semibold text-green-600 mt-2">
                PKR {s.balance.toFixed(2)}
              </p>

            </div>
          ))}

        </div>
      </div>

    </div>
  );
};

export default Dashboard;